import {
  type SuppliersPageChatTaxProfileFragment,
  type SuppliersPageChatTaxProfileListQuery,
} from '../../../../../../../generated';
import {
  type CustomerTaxProfileListResult,
  type CustomerTaxProfileRow,
} from '../types';

import { ABSENT_VALUE } from './taxJurisdictionMapper';

const DEFAULT_ERROR_MESSAGE = 'Failed to load customers.';

type ChatTaxProfileListResponse =
  SuppliersPageChatTaxProfileListQuery['chatTaxProfileList'];

/**
 * The distributor's own name for the customer comes first, because this table
 * is read from the distributor's side. A stale chat reference leaves the id as
 * the only thing to show.
 */
export const customerDisplayName = (
  chat: SuppliersPageChatTaxProfileFragment['chat']
): string =>
  chat.node?.supplierCustomerName ||
  chat.node?.restaurantName ||
  chat.node?.chatName ||
  ABSENT_VALUE;

const mapProfile = (
  profile: SuppliersPageChatTaxProfileFragment
): CustomerTaxProfileRow => {
  // Null when the jurisdiction was removed after the profile was set, so the
  // profile stands with no jurisdiction to describe.
  const jurisdiction = profile.jurisdiction.node ?? null;

  return {
    id: profile.id,
    chatId: profile.chat.id,
    customerName: customerDisplayName(profile.chat),
    customerNumber: profile.chat.node?.customerNumber ?? null,
    jurisdictionCode: jurisdiction?.code ?? null,
    countryCode: jurisdiction?.countryCode ?? null,
    rateCount: jurisdiction ? jurisdiction.rates.length : null,
    updatedAt: profile.updatedAt,
  };
};

/**
 * Maps a `ChatTaxProfileListResponse` union into a page of customer rows.
 * Returns `null` while there is no response yet; the failure members become
 * `{ kind: 'error' }`.
 */
export const mapChatTaxProfileResponse = (
  response: ChatTaxProfileListResponse | null | undefined
): CustomerTaxProfileListResult | null => {
  if (!response) {
    return null;
  }

  switch (response.__typename) {
    case 'ChatTaxProfileListResult':
      return {
        kind: 'success',
        page: {
          customers: (response.edges ?? []).map((edge) =>
            mapProfile(edge.node)
          ),
          pageInfo: {
            hasNextPage: response.pageInfo?.hasNextPage ?? false,
            endCursor: response.pageInfo?.endCursor ?? '',
          },
        },
      };
    case 'InputValidationFailure':
    case 'AuthorizationFailure':
      return {
        kind: 'error',
        message: response.message ?? DEFAULT_ERROR_MESSAGE,
      };
    default:
      return { kind: 'error', message: DEFAULT_ERROR_MESSAGE };
  }
};

/** Epoch seconds to a stable UTC stamp, e.g. `2026-09-10 08:15 UTC`. */
export const formatProfileUpdatedAt = (
  updatedAt: number | null | undefined
): string => {
  if (updatedAt === null || updatedAt === undefined) {
    return ABSENT_VALUE;
  }

  const stamp = new Date(updatedAt * 1000);

  if (Number.isNaN(stamp.getTime())) {
    return ABSENT_VALUE;
  }

  return `${stamp.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};
